/**
 * 12-Week Endurance Base Program
 *
 * For users who pick the `endurance` goal at onboarding. There was nothing
 * in the catalog targeting them — the tactical programs front-load
 * conditioning but bury it under strength work, and HST/Pure Strength
 * don't run at all.
 *
 * Built on the Lydiard / 80-20 polarized model: ~80% of weekly mileage at
 * easy aerobic pace (conversational, can-talk-in-full-sentences), ~20% at
 * threshold or VO2max intensity. Most amateur runners get this backwards —
 * too many medium-hard efforts that build fatigue without building the
 * aerobic engine. The hard days here are short and hard; the easy days are
 * actually easy.
 *
 * 5 days/week: 4 cardio + 1 strength. The strength day is *runner-specific*
 * — single-leg work, posterior chain, ankle/calf resilience, anti-rotation
 * core. NOT heavy compound lifting that would leave the legs cooked for
 * the next run.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'endurance-base-12week',
  name: '12-Week Endurance Base',
  description: 'Polarized run training — most miles easy, some miles fast. Builds an aerobic engine that supports tempo running, longer rucks, and faster recovery between hard sessions.',
  level: 'grunt', // intermediate tier — non-tactical programs share the grunt difficulty bucket
  duration: 12,
  daysPerWeek: 5,
  goals: ['endurance'],
  difficulty: 'intermediate',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // Week 1 + Week 12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Endurance Testing',
      focus: 'Run a 5k for time, log baseline weekly mileage',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Base Building — high mileage, low intensity.
    // The whole point is to put aerobic minutes in the bank.
    // intensity refers to the strength day; cardio intent lives in the
    // template details where it can be expressed as Zone 2 / tempo / etc.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Base Building',
      focus: '80% easy aerobic, 20% tempo. Build the engine.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.72,
        weeklyIncrease: 0.0175,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s',
      },
    },
    // Week 6: Cutback Week — reduce volume 30% for connective tissue
    // recovery. Endurance injuries usually arrive when volume creeps up
    // 4 weeks straight without a downshift.
    {
      weeks: [6],
      type: 'deload',
      name: 'Cutback Week',
      focus: 'Reduce volume 30%, keep one quality session, recover.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.7,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.65,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 0.7,
        restPeriod: '60-90s',
      },
    },
    // Weeks 7-10: Build Phase — add interval sessions, longer tempo runs.
    // The 20% hard work gets harder; the 80% easy work stays easy.
    {
      weeks: [7, 8, 9, 10],
      type: 'intensification',
      name: 'Build Phase',
      focus: 'Sharper intervals + longer tempos. Keep easy days truly easy.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.72,
        endIntensity: 0.80,
        weeklyIncrease: 0.02,
      },
      trainingParameters: {
        repRange: [6, 10],
        setsMultiplier: 1.0,
        restPeriod: '60-90s',
      },
    },
    // Week 11: Taper Week — reduce volume 50%, maintain intensity.
    // Sharpens the body for the W12 retest without erasing fitness.
    {
      weeks: [11],
      type: 'deload',
      name: 'Taper Week',
      focus: 'Cut volume in half, maintain a single quality session.',
      intensityMultiplier: 0.95,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.75,
        endIntensity: 0.75,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [6, 10],
        setsMultiplier: 0.5,
        restPeriod: '60-90s',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Endurance Test',
  sessionType: 'assessment',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Time Trial',
      type: 'testing',
      exercises: [
        { name: 'Run', duration: '1 mile', format: 'cardio', intensity: 'Max', details: 'Warm up 10 min easy, then run 1 mile as fast as you sustainably can. Record time.', sessionType: 'assessment' },
        { name: 'Run', duration: '5k', format: 'cardio', intensity: 'Hard', details: 'After 10 min recovery, run 5k at your race effort. Record time. Brzycki-equivalent for run pace — this is your training reference.', sessionType: 'assessment' },
      ],
    },
  ],
};

// Day 1 — Easy aerobic. The most important workout of the week is the one
// where you don't try hard. This is where the aerobic adaptations happen.
const easyRunDay: WorkoutTemplateDay = {
  name: 'Easy Aerobic Run',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Run', duration: '30-45 min', format: 'cardio', intensity: 'Easy', details: 'Conversational pace — should be able to speak in full sentences. If you can\'t, slow down. Heart rate ~70-75% of max.' },
      ],
    },
  ],
};

// Day 2 — Strength for runners. Single-leg, posterior chain, calves, core.
// No barbell back squats — they tax the same systems the runs do, and
// recovery would compromise Day 3's quality. Volume on accessories is
// moderate (3 sets each), staying RPE 7 and below.
const runnerStrengthDay: WorkoutTemplateDay = {
  name: 'Runner Strength',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before loading' },
        { name: 'Hip Opener Flow', sets: 2, reps: 10, format: 'recovery', details: 'Open hips before RDL and split squats — critical for running-stride patterns' },
      ],
    },
    {
      name: 'Posterior Chain + Single-Leg',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 8, intensity: 'RPE 7', details: 'Hinge pattern, slight knee bend. Builds the hamstrings and glutes that drive every stride.' },
        { name: 'Bulgarian Split Squat', sets: 3, reps: 10, intensity: 'RPE 7', details: 'Per leg. Rear foot elevated. Single-leg stability transfers directly to running.' },
        { name: 'Hip Thrust', sets: 3, reps: 10, intensity: 'RPE 7', details: 'Pause at the top, squeeze glutes. Strong glutes prevent hamstring strains.' },
      ],
    },
    {
      name: 'Resilience + Core',
      type: 'strength',
      exercises: [
        { name: 'Calf Raise', sets: 3, reps: 15, intensity: 'RPE 7', details: 'Slow tempo, full range. Seated (bent-knee) targets the soleus — the calf that carries endurance mileage.' },
        { name: 'Plank Hold', sets: 3, reps: '45s', format: 'bodyweight', details: 'Anti-extension. Brace abs, glutes squeezed. Core stability prevents wobble at mile 8.' },
        { name: 'Side Plank', sets: 2, reps: '30s', format: 'bodyweight', details: 'Per side. Lateral stability — fights hip drop, the cause of most runner knee pain.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Decompress and re-open hips after lifting — keeps running stride healthy' },
      ],
    },
  ],
};

// Day 3 — The 20% hard day. Alternates intervals and tempo across the
// program; details on the day spell out which to run. Keeps the hard work
// short (20-30 min total work) so the rest of the week stays easy.
const qualityDay: WorkoutTemplateDay = {
  name: 'Quality Session',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Tempo or Intervals',
      type: 'conditioning',
      format: 'intervals',
      exercises: [
        { name: 'Tempo Run', duration: '20-30 min', format: 'cardio', intensity: 'Hard', details: 'Choose ONE based on the week\'s focus: (a) Tempo: 20-30 min at comfortably-hard pace, ~85% max HR, "could speak only short phrases"; (b) Intervals: warm up, then 6-8x 400m hard / 400m walk-jog. Keep the work hard; the rest must be very easy.' },
      ],
    },
  ],
};

// Day 4 — Recovery cardio. Easy enough that it speeds recovery, hard enough
// to put aerobic minutes in the bank. Many runners skip this day; doing it
// at true Zone 2 is what separates polarized training from junk miles.
const recoveryDay: WorkoutTemplateDay = {
  name: 'Recovery Cardio',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Active Recovery',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Run', duration: '30-40 min', format: 'cardio', intensity: 'Easy', details: 'Slowest pace you\'ve run all week. If your watch is yelling about your heart rate, you\'re doing it right.' },
      ],
    },
  ],
};

// Day 5 — Long run. The single biggest aerobic stimulus of the week.
// Distance progresses across the program; details ride that progression.
const longRunDay: WorkoutTemplateDay = {
  name: 'Long Run',
  sessionType: 'pt',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Long Aerobic',
      type: 'conditioning',
      exercises: [
        { name: 'Run', duration: '60-90 min', format: 'cardio', intensity: 'Easy', details: 'Distance grows across the program — start at the lower end (60 min) in Base, build to 90+ min by Week 10. Conversational pace throughout. Fuel mid-run if longer than 75 min.' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const enduranceBase12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,

    // Training cycle (W2-11)
    'training-day1': easyRunDay,
    'training-day2': runnerStrengthDay,
    'training-day3': qualityDay,
    'training-day4': recoveryDay,
    'training-day5': longRunDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
